import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { useSidebar } from "../../SidebarContext";
import { decryptToken } from "../utils/getToken";
import { Copy, Eye, EyeOff, Pencil } from "lucide-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";


function Integration() {
    const location = useLocation();
    
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const token = decryptToken("accessToken") || "";
    const url = process.env.REACT_APP_LINK;
    const [otpModal, setOtpModal] = useState(false);
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);


    const webhookUrl =
        `${url}/leads/integrations/google-ads-leads`;

    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalIndex, setModalIndex] = useState(null);
    const [modalTitle, setModalTitle] = useState("");

    // Fetch integrations from backend
    useEffect(() => {
        const fetchIntegrations = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${url}/google-ads/${isLoggedIn.main_id}`, {
                    method: "GET",
                    headers: { Authorization: `Bearer ${token}` },
                });
                const res = await response.json();
                if (response.ok && Array.isArray(res.data)) {
                    setIntegrations(res.data || []);
                } else {
                    setIntegrations([{ title: "Google Ads Integration", webhook_key: "" }]);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchIntegrations();
    }, []);

    const handleGenerateKey = async (index) => {
        try {
            const response = await fetch(`${url}/google-ads/generate-key`, {
                method: "GET",
                headers: { Authorization: `Bearer ${token}` },
            });
            const res = await response.json();
            if (response.ok && res.webhook_key) {
                const updated = [...integrations];
                updated[index].webhook_key = res.webhook_key;
                updated[index].main_id = isLoggedIn.main_id;
                updated[index].main_name = isLoggedIn.full_name;
                setIntegrations(updated);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);

        toast.success("Copied to clipboard!");
    };

    const handleAddMore = () => {
        const nextIndex = integrations.length + 1; // sequence number
        setIntegrations([
            ...integrations,
            {
                title: `New Integration ${nextIndex}`,
                webhook_key: "",
            },
        ]);
    };



    const handleRemove = async (index) => {
        const integration = integrations[index];

        // SweetAlert confirmation for both backend and frontend
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "Do you want to delete this integration?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes",
            cancelButtonText: "Cancel",
            customClass: {
                confirmButton: 'swal-confirm-btn', // your custom class
                cancelButton: 'swal-cancel-btn'
            },
            buttonsStyling: false
        });

        if (!result.isConfirmed) return;

        // If integration exists in backend (has webhook_key), delete from backend
        if (integration.webhook_key) {
            try {
                setLoading(true);
                const response = await fetch(`${url}/google-ads/${integration.id}`, {
                    method: "DELETE",
                    headers: { Authorization: `Bearer ${token}` },
                });
                const res = await response.json();

                if (response.ok) {
                    Swal.fire({
                        title: "Integration has been deleted from backend.!",
                        icon: "success",
                        confirmButtonText: "OK",
                        customClass: {
                            confirmButton: 'swal-confirm-btn'
                        },
                        buttonsStyling: false
                    });

                    // Swal.fire("Deleted!", "Integration has been deleted from backend.", "success");
                } else {
                    Swal.fire("Error", res.message || "Failed to delete integration from backend", "error");
                    return;
                }
            } catch (error) {
                console.error(error);
                Swal.fire("Error", "Something went wrong while deleting from backend.", "error");
                return;
            } finally {
                setLoading(false);
            }
        } else {
            // Just remove from frontend
            Swal.fire({
                title: "Integration removed!",
                icon: "success",
                confirmButtonText: "OK",
                customClass: {
                    confirmButton: 'swal-confirm-btn'
                },
                buttonsStyling: false
            });

            // Swal.fire("Deleted!", "Integration removed", "success");
        }

        // Remove from frontend in all cases
        setIntegrations((prev) => prev.filter((_, i) => i !== index));
    };


    // Open modal to edit title
    const openModal = (index) => {
        setModalIndex(index);
        setModalTitle(integrations[index].title);
        setModalOpen(true);
    };

    // Save title from modal
    const saveModalTitle = () => {
        const updated = [...integrations];
        updated[modalIndex].title = modalTitle;
        setIntegrations(updated);
        setModalOpen(false);
    };


    const handleSubmit = async (index) => {
        try {
            setLoading(true);
            const integration = integrations[index];
            const method = integration.id ? "PUT" : "POST"; // existing: PUT, new: POST
            const endpoint = integration.id ? `${url}/google-ads/${integration.id}` : `${url}/google-ads`;

            const response = await fetch(endpoint, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(integration),
            });

            const res = await response.json();
            if (response.ok) {
                Swal.fire({
                    title: integration.id ? "Updated!" : "Created!",
                    text: `Integration "${integration.title}" saved successfully.`,
                    icon: "success",
                    confirmButtonText: "OK",
                    customClass: { confirmButton: "swal-confirm-btn" },
                    buttonsStyling: false,
                });

                // If it was new, update the integration in state with the id from backend
                if (!integration.id && res.data?.id) {
                    const updated = [...integrations];
                    updated[index].id = res.data.id;
                    setIntegrations(updated);
                }
            } else {
                // Swal.fire("Error", res.message || "Failed to save integration", "error");
            }
        } catch (error) {
            console.error(error);
            // Swal.fire("Error", "Something went wrong", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleTitleChange = (index, value) => {
        const updated = [...integrations];
        updated[index].title = value;
        setIntegrations(updated);
    };

    const handleVerifyPassword = async () => {
        try {
            const response = await fetch(`${url}/login`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: isLoggedIn?.email,
                    password,
                }),
            });

            const res = await response.json();
            if (response.ok) {
                localStorage.setItem("accessToken", res.token);
                toast.success("Verification successful!");

                setIntegrations((prev) =>
                    prev.map((item, i) =>
                        i === modalIndex ? { ...item, showKey: true } : item
                    )
                );

                setOtpModal(false);
                setPassword("");
            }
            else {
                toast.error(res.message || "Invalid password");
            }
        } catch (error) {
            toast.error("Something went wrong");
        }
    };




    return (
        <>
            <div className="oms-header">
                <h1 className="dashboard-title">Google Ads Integration</h1>
            </div>

            {/* Webhook URL at top only */}
            <div className="ecomadmin-card">
                <div className="ecomadmin-field">
                    <label className="ecomadmin-label">Webhook URL</label>
                    <div className="ecomadmin-webhook-display">
                        <span className="ecomadmin-webhook-text">{webhookUrl}</span>
                        <button className="ecomadmin-btn secondary" onClick={() => handleCopy(webhookUrl)}>
                            <Copy/>
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <p>Loading...</p>
            ) : (
                <div>
                    {integrations.map((integration, index) => (
                        <div className="ecomadmin-card" key={index}>
                            {/* Editable Title */}
                            <div className="ecomadmin-field">
                                <label className="ecomadmin-label">Integration Title</label>
                                <div className="ecomadmin-input-group">
                                    <input
                                        type="text"
                                        value={integration.title}
                                        onChange={(e) => handleTitleChange(index, e.target.value)}
                                        className="ecomadmin-input"
                                    />
                                    {/* {integration.id && (
                                        <button
                                            className="ecomadmin-btn edit-icon"
                                            title="Edit existing"
                                            onClick={() => openModal(index)}
                                        >
                                            <Pencil size={16} />
                                        </button>
                                    )} */}
                                </div>
                            </div>

                            {/* Webhook Key */}
                            {/* <div className="ecomadmin-field">
                                <label className="ecomadmin-label">Webhook Key</label>
                                <div className="ecomadmin-input-group">
                                    <input
                                        type="text"
                                        value={integration.webhook_key || ""}
                                        readOnly={!integration.id} // editable only if it has id
                                        onChange={(e) => {
                                            const updated = [...integrations];
                                            updated[index].webhook_key = e.target.value;
                                            setIntegrations(updated);
                                        }}
                                        className="ecomadmin-input"
                                    />
                                    {!integration.id && (
                                        <button
                                            className="ecomadmin-btn primary"
                                            onClick={() => handleGenerateKey(index)}
                                        >
                                            Generate Key
                                        </button>
                                    )}
                                </div>
                            </div> */}


                            <div className="ecomadmin-field">
                                <label className="ecomadmin-label">Webhook Key</label>
                                <div className="ecomadmin-input-group" style={{ position: "relative" }}>
                                    <input
                                        type={integration.showKey ? "text" : "password"}
                                        value={integration.webhook_key || ""}
                                        readOnly
                                        className="ecomadmin-input"
                                    />

                                    {integration.id ? (
                                        <>
                                            {/* Eye button */}
                                            <button
                                                className="ecomadmin-btn secondary"
                                                type="button"
                                                onClick={() => {
                                                    if (!integration.showKey) {
                                                        setModalIndex(index);
                                                        setOtpModal(true);
                                                    } else {
                                                        const updated = [...integrations];
                                                        updated[index].showKey = false;
                                                        setIntegrations(updated);
                                                    }
                                                }}
                                                style={{ marginLeft: "8px" }}
                                            >
                                                {integration.showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>

                                            {/* Copy button only if visible */}
                                            {integration.showKey && (
                                                <button
                                                    className="ecomadmin-btn secondary"
                                                    type="button"
                                                    onClick={() => handleCopy(integration.webhook_key)}
                                                    style={{ marginLeft: "8px" }}
                                                >
                                                    Copy
                                                </button>
                                            )}
                                        </>
                                    ) : (
                                        // For unsaved integration
                                        <button
                                            className="ecomadmin-btn primary"
                                            onClick={() => handleGenerateKey(index)}
                                        >
                                            Generate Key
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Webhook Key */}



                            {/* Actions */}
                            <div className="ecomadmin-displaygoogleads">
                                <div className="ecomadmin-actions">
                                    <button className="ecomadmin-btn danger" onClick={() => handleRemove(index)}>
                                        Remove
                                    </button>
                                </div>
                                <div className="ecomadmin-actions">
                                    <button className="ecomadmin-btn primary" onClick={() => handleSubmit(index)}>
                                        {integration.id ? "Update" : "Submit"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}


                    {/* Add More & Submit */}
                    <div className="ecomadmin-displaygoogleads">
                        <div className="ecomadmin-actions">
                            <button className="ecomadmin-btn add" onClick={handleAddMore}>
                                + Add More
                            </button>
                        </div>

                        {/* {integrations.length > 0 && (
                            <div className="ecomadmin-actions">
                                <button className="ecomadmin-btn primary" onClick={handleSubmit} disabled={loading}>
                                    {loading ? "Saving..." : "Submit All"}
                                </button>
                            </div>
                        )} */}
                    </div>
                </div>
            )}

            {/* Modal */}
            {modalOpen && (
                <div className="ecomadmin-modal-overlay">
                    <div className="ecomadmin-modal">
                        <h2>Edit Integration Title</h2>
                        <input
                            type="text"
                            value={modalTitle}
                            onChange={(e) => setModalTitle(e.target.value)}
                            className="ecomadmin-input"
                        />
                        <div className="ecomadmin-actions" style={{ marginTop: "10px" }}>
                            <button className="ecomadmin-btn primary" onClick={saveModalTitle}>
                                Save
                            </button>
                            <button className="ecomadmin-btn secondary" onClick={() => setModalOpen(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {otpModal && (
                <div className="setting-cart-modal">
                    <div className="setting-cart-modal-content">
                        <h3 className="setting-payment-modal-title" style={{ textAlign: "center" }}>
                            Verification
                        </h3>

                        <label className="setting-payment-label">Enter Password</label>
                        <div style={{ position: "relative" }}>
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                className="setting-payment-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        handleVerifyPassword(); // trigger verify
                                    }
                                }}
                            />
                            <span
                                style={{
                                    position: "absolute",
                                    right: "10px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    cursor: "pointer",
                                }}
                                onClick={() => setShowPassword((prev) => !prev)}
                            >
                                {showPassword ? <EyeOff /> : <Eye />}
                            </span>
                        </div>

                        <div className="setting-payment-modal-actions">
                            <button className="setting-payment-save" onClick={handleVerifyPassword}>
                                Verify
                            </button>
                            <button
                                className="setting-payment-cancel"
                                onClick={() => setOtpModal(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Integration;
